import React, { useEffect, useRef, useState } from 'react';
import socket from './socket';
import './App.css';

// Fallback poll interval in case a socket event is ever missed (network hiccup, etc).
const FALLBACK_POLL_MS = 15000;
const HIGHLIGHT_MS = 3000;

function formatOutput(score, type) {
   if (!score && score !== 0) return 'N/A';

   switch (type?.toLowerCase()) {
      case 'rulerofthehill': {
         const points = parseFloat(score);
         if (isNaN(points)) return 'Invalid Score';
         return `${Math.round(points).toLocaleString()} \u{1F451}`;
      }
      case 'time': {
         const totalSeconds = parseFloat(score);
         if (isNaN(totalSeconds)) return 'Invalid Time';
         const minutes = Math.floor(totalSeconds / 60);
         const seconds = Math.floor(totalSeconds % 60);
         const milliseconds = Math.round((totalSeconds % 1) * 1000);
         return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
      }
      case 'distance': {
         const distance = parseFloat(score);
         if (isNaN(distance)) return 'Invalid Distance';
         return `${distance.toFixed(2)} ft`;
      }
      case 'points':
      case 'score':
      default: {
         const regularPoints = parseFloat(score);
         if (isNaN(regularPoints)) return 'Invalid Score';
         return Math.round(regularPoints).toLocaleString();
      }
   }
}

function getScoringMessage(type, isRulerOfHillMode) {
   if (!type) return '';
   if (isRulerOfHillMode || type.toLowerCase() === 'rulerofthehill') {
      return 'Ruler of the Hill Wins';
   }
   switch (type.toLowerCase()) {
      case 'time':
         return 'Fastest Time Wins';
      case 'distance':
         return 'Farthest Distance Wins';
      case 'points':
      case 'score':
      default:
         return 'Highest Score Wins';
   }
}

// Renders the live board for a single game. Driven by `scoreUpdate` socket
// events for near-instant updates, with a slow poll as a safety net.
const LiveScoreboardPanel = ({ game }) => {
   const [scores, setScores] = useState([]);
   const [scoringType, setScoringType] = useState(game.scoringType || '');
   const [isRulerOfHill, setIsRulerOfHill] = useState(false);
   const [error, setError] = useState('');
   const [risingPlayers, setRisingPlayers] = useState({});
   const previousRanks = useRef({});
   const highlightTimers = useRef({});

   const fetchResults = async () => {
      try {
         const response = await fetch(`/topScores?game=${game.shortName}`);
         if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
         }
         const data = await response.json();
         const nextScores = data.players || [];

         // Figure out who moved up in rank since the last fetch so we can
         // flash a highlight on them (an "overtake").
         const nextRanks = {};
         nextScores.forEach((s, index) => {
            nextRanks[s.playerName] = index;
         });

         const risen = {};
         nextScores.forEach((s) => {
            const prevRank = previousRanks.current[s.playerName];
            const nextRank = nextRanks[s.playerName];
            if (prevRank !== undefined && nextRank < prevRank) {
               risen[s.playerName] = true;
            }
         });

         if (Object.keys(risen).length > 0) {
            setRisingPlayers((prev) => ({ ...prev, ...risen }));
            Object.keys(risen).forEach((playerName) => {
               clearTimeout(highlightTimers.current[playerName]);
               highlightTimers.current[playerName] = setTimeout(() => {
                  setRisingPlayers((prev) => {
                     const next = { ...prev };
                     delete next[playerName];
                     return next;
                  });
               }, HIGHLIGHT_MS);
            });
         }

         previousRanks.current = nextRanks;
         setScores(nextScores);
         setScoringType(data.scoringType || '');
         setIsRulerOfHill(data.isRulerOfHill || false);
         setError('');
      } catch (err) {
         console.error('Error fetching live scores:', err);
         setError('Failed to load scores.');
      }
   };

   useEffect(() => {
      fetchResults();

      const onScoreUpdate = (payload) => {
         if (payload?.gameId === game.id) {
            fetchResults();
         }
      };
      socket.on('scoreUpdate', onScoreUpdate);

      const interval = setInterval(fetchResults, FALLBACK_POLL_MS);
      const timers = highlightTimers.current;

      return () => {
         socket.off('scoreUpdate', onScoreUpdate);
         clearInterval(interval);
         Object.values(timers).forEach(clearTimeout);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [game.id, game.shortName]);

   const message = getScoringMessage(scoringType, isRulerOfHill);

   return (
      <div className="scoreboard-container live-scoreboard-panel">
         <div className="scoreboard-header">
            <h1 className="game-title">{game.displayName}</h1>
         </div>

         {error && <div className="error-message margin-top-medium">{error}</div>}

         {!error && (
            scores.length > 0 ? (
               <div className="scoreboard-table-wrapper">
                  <table className="scoreboard-table margin-top-medium">
                     <thead>
                        <tr>
                           {isRulerOfHill ? (
                              <th colSpan="3">{'\u{1F451}'} Current Ruler of the Hill {'\u{1F451}'}</th>
                           ) : (
                              <>
                                 <th>Rank</th>
                                 <th>Player Name</th>
                                 <th>Score</th>
                              </>
                           )}
                        </tr>
                     </thead>
                     <tbody>
                        {isRulerOfHill ? (
                           <tr className="ruler-of-hill-row">
                              <td colSpan="3" style={{ textAlign: 'center', fontSize: '24px', padding: '20px' }}>
                                 {scores[0].playerName || 'Unknown Player'}
                              </td>
                           </tr>
                        ) : (
                           scores.map((score, index) => (
                              <tr
                                 key={score.playerName}
                                 className={risingPlayers[score.playerName] ? 'score-row-rising' : ''}
                              >
                                 <td>{index + 1}</td>
                                 <td>{score.playerName || 'Unknown Player'}</td>
                                 <td>{formatOutput(score.score, scoringType)}</td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>

                  {message && (
                     <div className="scoreboard-footer">
                        <p className="text-center"><strong>{message}</strong></p>
                     </div>
                  )}
               </div>
            ) : (
               <div className="margin-top-medium">
                  <p>No scores recorded yet for this game.</p>
               </div>
            )
         )}
      </div>
   );
};

export default LiveScoreboardPanel;
